import React, { useEffect, useState } from "react";
import { Autocomplete, Chip, TextField, CircularProgress, Box, Typography } from "@mui/material";
import axios from "axios";
import { hostname } from "../utils";
import { getTagColor } from "../dashboard/lists/utils/tagColors";

interface TagOption {
  name: string;
  description?: string;
  source?: string;
}

interface TagsAutocompleteProps {
  value: string[];
  onChange: (tags: string[]) => void;
  companyId: string;
  getToken: () => Promise<string>;
  label?: string;
  placeholder?: string;
  freeSolo?: boolean;
  multiple?: boolean;
  disabled?: boolean;
  style?: React.CSSProperties;
}

const TagsAutocomplete: React.FC<TagsAutocompleteProps> = ({
  value,
  onChange,
  companyId,
  getToken,
  label = "Tags",
  placeholder = "Add Tags",
  freeSolo = true,
  multiple = true,
  disabled = false,
  style,
}) => {
  const [availableTags, setAvailableTags] = useState<TagOption[]>([]);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    const fetchTags = async () => {
      if (!companyId) return;

      setLoading(true);
      try {
        const token = await getToken();
        const response = await axios.get(
          `${hostname}/companies/${companyId}/tags`,
          {
            headers: {
              Authorization: `Bearer ${token}`,
            },
          }
        );
        setAvailableTags(response.data.tags || []);
      } catch (error) {
        console.error("Error fetching tags:", error);
        setAvailableTags([]);
      } finally {
        setLoading(false);
      }
    };

    fetchTags();
  }, [companyId, getToken]);

  return (
    <Autocomplete
      multiple={multiple}
      freeSolo={freeSolo}
      options={availableTags}
      value={value}
      onChange={(event, newValue) => {
        if (multiple) {
          // Convert TagOption objects and strings to string array
          const stringValues = (newValue as (string | TagOption)[]).map((item) =>
            typeof item === "string" ? item : item.name
          );
          onChange(stringValues);
        } else {
          const singleValue = newValue as string | TagOption | null;
          if (!singleValue) {
            onChange([]);
          } else if (typeof singleValue === "string") {
            onChange([singleValue]);
          } else {
            onChange([singleValue.name]);
          }
        }
      }}
      getOptionLabel={(option) => {
        if (typeof option === "string") return option;
        return option.name;
      }}
      isOptionEqualToValue={(option, value) => {
        const optionName = typeof option === "string" ? option : option.name;
        const valueName = typeof value === "string" ? value : (value as TagOption).name;
        return optionName === valueName;
      }}
      renderOption={(props, option) => {
        const tagOption = typeof option === "string"
          ? { name: option, description: "" }
          : option;

        return (
          <Box component="li" {...props}>
            <Box>
              <Typography variant="body2">{tagOption.name}</Typography>
              {tagOption.description && (
                <Typography variant="caption" color="text.secondary">
                  {tagOption.description}
                </Typography>
              )}
            </Box>
          </Box>
        );
      }}
      disabled={disabled}
      loading={loading}
      style={style}
      renderTags={(value: readonly TagOption[], getTagProps) =>
        value.map((option: TagOption, index: number) => {
          const label = typeof option === "string" ? option : option.name;
          const color = getTagColor(label);
          return (
            <Chip
              label={label}
              {...getTagProps({ index })}
              key={label}
              sx={{
                backgroundColor: color.bg,
                color: color.text,
                border: `1px solid ${color.border}`,
                fontWeight: 500,
                "& .MuiChip-deleteIcon": {
                  color: color.text,
                  opacity: 0.7,
                  "&:hover": { color: color.text, opacity: 1 },
                },
              }}
            />
          );
        })
      }
      renderInput={(params) => (
        <TextField
          {...params}
          label={label}
          placeholder={placeholder}
          InputProps={{
            ...params.InputProps,
            endAdornment: (
              <>
                {loading ? <CircularProgress color="inherit" size={20} /> : null}
                {params.InputProps.endAdornment}
              </>
            ),
          }}
        />
      )}
    />
  );
};

export default TagsAutocomplete;